import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, Plus, Search, Edit2, Trash2 } from 'lucide-react';
import { Card, Button, Input, Modal, Badge } from '../components/UI';
import Pagination from '../components/Pagination';
import { paymentMethodAPI } from '../api/apiClient';
import toast from 'react-hot-toast';

const PAYMENT_METHODS_PER_PAGE = 20;

const defaultFormState = {
  name: '',
  accountName: '',
  paymentNumber: '',
  paymentType: 'Bank Transfer',
  merchantType: 'Business',
  instructions: '',
  logoUrl: '',
  qrCodeUrl: '',
  isActive: true,
  displayOrder: 1,
};

const PaymentMethodsPage = () => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [formData, setFormData] = useState(defaultFormState);

  const loadPaymentMethods = useCallback(async () => {
    try {
      setLoading(true);
      const response = await paymentMethodAPI.getAll(
        page,
        PAYMENT_METHODS_PER_PAGE,
        search,
        statusFilter
      );
      const data = response.data.data;
      setPaymentMethods(data.paymentMethods || []);
      setTotalPages(data.pagination.pages || 1);
    } catch (error) {
      toast.error('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    loadPaymentMethods();
  }, [loadPaymentMethods]);

  const resetForm = () => {
    setFormData(defaultFormState);
    setEditingMethod(null);
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (method) => {
    setEditingMethod(method);
    setFormData({
      name: method.name || '',
      accountName: method.accountName || '',
      paymentNumber: method.paymentNumber || '',
      paymentType: method.paymentType || 'Bank Transfer',
      merchantType: method.merchantType || 'Business',
      instructions: method.instructions || '',
      logoUrl: method.logoUrl || '',
      qrCodeUrl: method.qrCodeUrl || '',
      isActive: method.isActive,
      displayOrder: method.displayOrder || 1,
    });
    setIsModalOpen(true);
  };

  const handleSavePaymentMethod = async () => {
    try {
      const {
        name,
        accountName,
        paymentNumber,
        paymentType,
        merchantType,
        instructions,
        logoUrl,
        qrCodeUrl,
        isActive,
        displayOrder,
      } = formData;

      if (!name || !accountName || !paymentNumber) {
        toast.error('Name, account name, and payment number are required');
        return;
      }

      if (editingMethod) {
        await paymentMethodAPI.update(editingMethod._id, {
          name,
          accountName,
          paymentNumber,
          paymentType,
          merchantType,
          instructions,
          logoUrl,
          qrCodeUrl,
          isActive,
          displayOrder,
        });
        toast.success('Payment method updated successfully');
      } else {
        await paymentMethodAPI.create({
          name,
          accountName,
          paymentNumber,
          paymentType,
          merchantType,
          instructions,
          logoUrl,
          qrCodeUrl,
          isActive,
          displayOrder,
        });
        toast.success('Payment method created successfully');
      }

      setIsModalOpen(false);
      resetForm();
      loadPaymentMethods();
    } catch (error) {
      toast.error(editingMethod ? 'Failed to update payment method' : 'Failed to create payment method');
    }
  };

  const handleDeletePaymentMethod = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payment method?')) {
      return;
    }

    try {
      await paymentMethodAPI.delete(id);
      toast.success('Payment method deleted successfully');
      loadPaymentMethods();
    } catch (error) {
      toast.error('Failed to delete payment method');
    }
  };

  const handleToggleStatus = async (method) => {
    try {
      const newStatus = method.isActive ? 'inactive' : 'active';
      await paymentMethodAPI.toggleStatus(method._id, newStatus);
      toast.success('Payment method status updated');
      loadPaymentMethods();
    } catch (error) {
      toast.error('Failed to update payment method status');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center space-x-3">
            <Wallet size={32} className="text-purple-400" />
            <span>Payment Methods</span>
          </h1>
          <p className="text-gray-400 mt-2">Manage payment options and merchant instructions for subscriptions.</p>
        </div>
        <Button variant="primary" onClick={openCreateModal} className="flex items-center space-x-2">
          <Plus size={18} />
          <span>Add Payment Method</span>
        </Button>
      </div>

      <Card>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-3 text-gray-500" size={18} />
            <input
              type="text"
              placeholder="Search payment methods..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant={statusFilter === '' ? 'primary' : 'secondary'}
              onClick={() => {
                setStatusFilter('');
                setPage(1);
              }}
              className="flex-1"
            >
              All
            </Button>
            <Button
              variant={statusFilter === 'active' ? 'primary' : 'secondary'}
              onClick={() => {
                setStatusFilter('active');
                setPage(1);
              }}
              className="flex-1"
            >
              Active
            </Button>
            <Button
              variant={statusFilter === 'inactive' ? 'secondary' : 'secondary'}
              onClick={() => {
                setStatusFilter('inactive');
                setPage(1);
              }}
              className="flex-1"
            >
              Inactive
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        {loading ? (
          <div className="text-center py-10">
            <div className="inline-block w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : paymentMethods.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-400">No payment methods found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-700 text-sm text-gray-400">
                  <th className="px-4 py-3">Logo</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Account</th>
                  <th className="px-4 py-3">Number</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Merchant</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Order</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paymentMethods.map((method) => (
                  <tr key={method._id} className="border-b border-gray-700 hover:bg-gray-700/50">
                    <td className="px-4 py-4">
                      {method.logoUrl ? (
                        <img
                          src={method.logoUrl}
                          alt={method.name}
                          className="h-10 w-10 rounded object-cover bg-gray-600"
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"%3E%3Crect x="3" y="3" width="18" height="18" rx="2"/%3E%3C/svg%3E';
                          }}
                        />
                      ) : (
                        <div className="h-10 w-10 rounded bg-gray-600 flex items-center justify-center text-xs text-gray-400">No Image</div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-white font-medium">{method.name}</td>
                    <td className="px-4 py-4 text-gray-300">{method.accountName}</td>
                    <td className="px-4 py-4 text-gray-300">{method.paymentNumber}</td>
                    <td className="px-4 py-4 text-gray-300">{method.paymentType}</td>
                    <td className="px-4 py-4 text-gray-300">{method.merchantType}</td>
                    <td className="px-4 py-4">
                      <Badge variant={method.isActive ? 'success' : 'danger'}>
                        {method.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-4 py-4 text-gray-300">{method.displayOrder}</td>
                    <td className="px-4 py-4 space-x-2">
                      <Button
                        variant="secondary"
                        onClick={() => handleToggleStatus(method)}
                        className="px-3 py-1"
                      >
                        {method.isActive ? 'Disable' : 'Enable'}
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => openEditModal(method)}
                        className="px-3 py-1"
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => handleDeletePaymentMethod(method._id)}
                        className="px-3 py-1"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={(newPage) => {
              if (newPage >= 1 && newPage <= totalPages) {
                setPage(newPage);
              }
            }}
          />
        </div>
      </Card>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={editingMethod ? 'Edit Payment Method' : 'Add Payment Method'}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSavePaymentMethod}>
              {editingMethod ? 'Save Changes' : 'Create Payment Method'}
            </Button>
          </>
        }
      >
        <div className="grid gap-4">
          <div className="flex items-start justify-between mb-4 p-4 bg-gray-700/50 rounded-lg">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-300 mb-2">Logo Preview</label>
              {formData.logoUrl ? (
                <div className="flex items-center space-x-4">
                  <img
                    src={formData.logoUrl}
                    alt="Payment method logo preview"
                    className="h-20 w-20 rounded object-cover bg-gray-600 border border-gray-500"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      document.getElementById('logo-error-msg').style.display = 'block';
                    }}
                  />
                  <div id="logo-error-msg" style={{ display: 'none' }} className="text-red-400 text-sm">
                    Invalid image URL
                  </div>
                </div>
              ) : (
                <div className="h-20 w-20 rounded bg-gray-600 flex items-center justify-center text-gray-400 border border-gray-500">
                  <span className="text-xs text-center">No Image</span>
                </div>
              )}
            </div>
          </div>
          <Input
            label="Payment Method Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Bkash, Nagad, Rocket"
          />
          <Input
            label="Account Name"
            value={formData.accountName}
            onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
            placeholder="Name of the account holder"
          />
          <Input
            label="Payment Number"
            value={formData.paymentNumber}
            onChange={(e) => setFormData({ ...formData, paymentNumber: e.target.value })}
            placeholder="e.g., +880XXXXXXXXX"
          />
          <Input
            label="Logo URL"
            value={formData.logoUrl}
            onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
            placeholder="https://example.com/logo.png"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Payment Type</label>
              <select
                value={formData.paymentType}
                onChange={(e) => setFormData({ ...formData, paymentType: e.target.value })}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option>Bank Transfer</option>
                <option>Mobile Money</option>
                <option>Cash</option>
                 <option>Cash Out</option>
                  <option>Send Money</option>
                <option>Crypto</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Merchant Type</label>
              <select
                value={formData.merchantType}
                onChange={(e) => setFormData({ ...formData, merchantType: e.target.value })}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option>Business</option>
                <option>Individual</option>
                <option>Merchant</option>
                <option>Agent</option>
                <option>Personal</option>
                <option>Other</option>
              </select>
            </div>
          </div>
          <Input
            label="QR Code URL"
            value={formData.qrCodeUrl}
            onChange={(e) => setFormData({ ...formData, qrCodeUrl: e.target.value })}
            placeholder="https://example.com/qrcode.png"
          />
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Instructions</label>
            <textarea
              value={formData.instructions}
              onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Display Order"
              type="number"
              min="1"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: Number(e.target.value) })}
            />
            <div className="flex items-center space-x-3 pt-6">
              <input
                id="isActive"
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500"
              />
              <label htmlFor="isActive" className="text-white font-medium">
                Active payment method
              </label>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PaymentMethodsPage;
